import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Button, Col, Row } from 'reactstrap';
import { Features, Person, ReduxState } from 'types';
import { useDeepCompareEffectNoCheck } from 'use-deep-compare-effect';
import { useConfirmApiWithDefault } from 'utils/api/ApiHooks';
import {
  DEFAULT_STATUS,
  DEVELOPMENT_PLANS_BASE_URL_PATH,
  DevelopmentPlan as DevelopmentPlanType,
  STATUS_URL_TO_VALUE_CONVERTER,
  STATUS_VALUE_TO_URL_CONVERTER,
} from 'utils/models/DevelopmentPlan';
import { useStateQueryParam } from 'utils/util/hooks';
import Page from 'views/Layout/Pages/Page';
import EmptyState from 'views/Widgets/EmptyState';
import Loading from 'views/Widgets/Loading';
import DevelopmentPlan from './DevelopmentPlan';
import DevelopmentPlansFeatureDisabled from './DevelopmentPlansFeatureDisabled';
import DevelopmentPlansTable from './DevelopmentPlansTable';
import ModalDevelopmentPlanEditorButton from './ModalDevelopmentPlanEditorButton';

interface Props {
  embedded?: boolean;
  person?: Person;
  hideHeaderControls?: boolean;
  features: Features;
}

const DevelopmentPlansDashboard: FC<Props> = ({
  embedded = false,
  person,
  hideHeaderControls = false,
  features,
}) => {
  const { formatMessage } = useIntl();

  const [filterStatuses, setFilterStatuses] = useStateQueryParam<string[]>(
    'status',
    DEFAULT_STATUS,
    STATUS_URL_TO_VALUE_CONVERTER,
    STATUS_VALUE_TO_URL_CONVERTER
  );
  const [selectedDevelopmentPlanId, setSelectedDevelopmentPlanId] = useState<
    number | undefined
  >(undefined);

  const [developmentPlans, setDevelopmentPlans] = useState<
    DevelopmentPlanType[]
  >([]);

  const toggleFilterStatuses = useCallback(
    (status) => {
      if (!filterStatuses.includes(status)) {
        setFilterStatuses([...filterStatuses, status]);
      } else {
        setFilterStatuses(filterStatuses.filter((i) => i !== status));
      }
    },
    [setFilterStatuses, filterStatuses]
  );

  const { data, status } = useConfirmApiWithDefault<DevelopmentPlanType[]>({
    method: 'GET',
    url: DEVELOPMENT_PLANS_BASE_URL_PATH,
    defaultValue: [],
    params: { person_id: person?.id },
  });

  useDeepCompareEffectNoCheck(() => {
    setDevelopmentPlans(data);
  }, [data]);

  const processedDevelopmentPlans = useMemo(
    () =>
      developmentPlans.map((developmentPlan) => ({
        ...developmentPlan,
        action: (
          <span>
            <Button
              className="btn-sm"
              color="light"
              onClick={() => setSelectedDevelopmentPlanId(developmentPlan.id)}
            >
              <FormattedMessage
                id="app.views..development_plans.development_plans_dashboard.review_packet"
                defaultMessage="Review plan"
              />
            </Button>
          </span>
        ),
      })),
    [developmentPlans]
  );

  const handleUpdateDetailsCallback = useCallback((x: DevelopmentPlanType) => {
    setDevelopmentPlans((developmentPlans: DevelopmentPlanType[]) =>
      developmentPlans.map((p) => (p.id === x.id ? x : p))
    );
  }, []);

  const handleAddNewItemCallback = useCallback((x: DevelopmentPlanType) => {
    setDevelopmentPlans((developmentPlans: DevelopmentPlanType[]) => [
      x,
      ...developmentPlans,
    ]);
  }, []);

  if (!features?.development_plans?.enabled) {
    return <DevelopmentPlansFeatureDisabled />;
  }

  return (
    <>
      {selectedDevelopmentPlanId && (
        <DevelopmentPlan
          developmentPlanId={selectedDevelopmentPlanId}
          inModal={true}
          toggle={() => setSelectedDevelopmentPlanId(undefined)}
          onClosed={() => setSelectedDevelopmentPlanId(undefined)}
          callback={handleUpdateDetailsCallback}
        />
      )}
      <Page
        embedded={embedded}
        title={formatMessage({
          id: 'app.views.development_plans.development_plans_dashboard.title.development_plans',
          defaultMessage: 'Development plans',
        })}
      >
        {!embedded && (
          <Row>
            <Col className="col-auto">
              <ModalDevelopmentPlanEditorButton
                title={formatMessage({
                  id: 'app.views.development_plans.development_plans_dashboard.title.create_development_plan',
                  defaultMessage: 'Create development plan',
                })}
                callback={handleAddNewItemCallback}
              />
            </Col>
          </Row>
        )}
        {status == 'LOADING' && (
          <Loading
            message={formatMessage({
              id: 'app.views.development_plans.development_plans_dashboard.message.loading_development_plans',
              defaultMessage: 'Loading development plans...',
            })}
          />
        )}
        {status == 'ERROR' && (
          <EmptyState
            title={formatMessage({
              id: 'app.views.development_plans.development_plans_dashboard.title.error_loading_development_plans',
              defaultMessage: 'Error loading development plans',
            })}
            subtitle={formatMessage({
              id: 'app.views.development_plans.development_plans_dashboard.subtitle.please_try_again_later',
              defaultMessage: 'Please try again later',
            })}
          />
        )}
        {status == 'SUCCESS' && !developmentPlans?.length && (
          <EmptyState
            wrapInCard={true}
            title={formatMessage({
              id: 'app.views.development_plans.development_plans_dashboard.title.you_do_not_have_any_promotion_packets',
              defaultMessage:
                'You do not have any development plans that you are eligible to view',
            })}
            subtitle={formatMessage({
              id: 'app.views.development_plans.development_plans_dashboard.subtitle.if_you_are_a_people_manager_or_yourself',
              defaultMessage:
                'If you are a people manager, you can create a new development plan for anyone that you manage. If your organization allows, you can also create a development plan for yourself.',
            })}
          >
            {/* {newPacketButton} */}
          </EmptyState>
        )}
        {status == 'SUCCESS' && !!developmentPlans?.length && (
          <DevelopmentPlansTable
            developmentPlans={processedDevelopmentPlans}
            filterStatuses={filterStatuses}
            toggleFilterStatuses={toggleFilterStatuses}
            hideHeaderControls={hideHeaderControls}
          />
        )}
      </Page>
    </>
  );
};

const mapStateToProps = (state: ReduxState) => {
  const { features } = state;

  return {
    features,
  };
};

export default connect(mapStateToProps)(React.memo(DevelopmentPlansDashboard));
